import { FEATURE_FLAGS } from '@/constants/feature-flags';
import { MANAGER_LABELS } from '@/constants';
import { ManagerDashboardContentProps } from '../ManagerDashboardContent/ManagerDashboardContent';
import { SelectableCard } from '@/components/Cards';
import { TManagerDashboardFilter } from '@/features/manager-dashboard';
import React from 'react';

/** Props for the ManagerDashboardFilterCards component */
export interface ManagerDashboardFilterCardsProps {
  /** The currently selected summary-card filter */
  activeFilter: TManagerDashboardFilter;
  /** Whether the filter cards are disabled */
  isDisabled: boolean;
  /** Callback to apply a filter when a summary card is selected */
  onSelect: ManagerDashboardContentProps['handleFilterSelect'];
  /** Number of users for the selected product before filtering */
  users: number;
  /** Number of users in the certified bucket */
  usersCertified: number;
  /** Number of users needing attention */
  usersNeedingAttention: number;
  /** Number of users who passed practice */
  usersPassedPractice: number;
}

/**
 * ManagerDashboardFilterCards Component
 *
 * Renders summary filter cards for manager dashboard quick filtering.
 */
export const ManagerDashboardFilterCards: React.FC<ManagerDashboardFilterCardsProps> = ({
  activeFilter,
  isDisabled,
  onSelect,
  users,
  usersCertified,
  usersNeedingAttention,
  usersPassedPractice,
}) => {
  /**
   * Column wrapper for filter cards.
   */
  const Column = ({ children }: { children: React.ReactNode }) => (
    <div className="col-span-12 md:col-span-6 lg:col-span-5 xl:col-span-4">{children}</div>
  );

  /**
   * Renders the certification filter card, which shows either final certification or
   * practice certification based on the feature flag. Displays the number of users
   * certified/practice passed out of the total users for context.
   */
  const CertificationColumn = () => (
    <Column>
      {FEATURE_FLAGS.ENABLE_FINAL_CERTIFICATION ? (
        <SelectableCard
          disabled={isDisabled}
          icon="Medal"
          onSelect={() => onSelect('certified')}
          selected={activeFilter === 'certified'}
          text={MANAGER_LABELS.FILTER_CERTIFIED}
          valuePrimary={usersCertified}
          valueSecondary={users}
          variant="success"
        />
      ) : (
        <SelectableCard
          disabled={isDisabled}
          icon="Medal"
          onSelect={() => onSelect('practicePassed')}
          selected={activeFilter === 'practicePassed'}
          text={MANAGER_LABELS.FILTER_PRACTICE}
          valuePrimary={usersPassedPractice}
          valueSecondary={users}
          variant="success"
        />
      )}
    </Column>
  );

  /**
   * Renders the needs attention filter card, which shows the number of users needing attention
   * out of the total users. This card is always shown regardless of feature flags, as it is a
   * key part of the manager's ability to quickly identify team members who may require support.
   */
  const AttentionColumn = () => (
    <Column>
      <SelectableCard
        disabled={isDisabled}
        icon="Warning"
        onSelect={() => onSelect('needsAttention')}
        selected={activeFilter === 'needsAttention'}
        text={MANAGER_LABELS.FILTER_NEEDS_ATTENTION}
        valuePrimary={usersNeedingAttention}
        valueSecondary={users}
        variant="error"
      />
    </Column>
  );

  // ================================
  // Render
  // ================================
  return (
    <div className="grid grid-cols-1 gap-2 md:grid-cols-12 lg:gap-6">
      <CertificationColumn />
      <AttentionColumn />
    </div>
  );
};

ManagerDashboardFilterCards.displayName = 'ManagerDashboardFilterCards';

export default ManagerDashboardFilterCards;
